
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";

/* =========================================================
   BASE URL
   ========================================================= */

const baseUrl =
  process.env.REACT_APP_BACKEND_BASE_URL;

console.log("baseurl:", baseUrl);

/* =========================================================
   SAFE LOCAL STORAGE PARSE
   ========================================================= */

const safeParse = (key) => {
  try {
    const value = localStorage.getItem(key);

    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

/* =========================================================
   LOGIN USER
   ========================================================= */

export const loginUser = createAsyncThunk(
  "auth/loginUser",

  async (
    { username, password, role },
    { rejectWithValue }
  ) => {
    try {
      /* -----------------------------------------------------
         SELECT LOGIN ENDPOINT
         ----------------------------------------------------- */

      const loginUrl =
        role === "business"
          ? `${baseUrl}/business/login`
          : `${baseUrl}/users/login`;

      /* -----------------------------------------------------
         LOGIN REQUEST
         ----------------------------------------------------- */

      const response = await axios.post(
        loginUrl,
        {
          username,
          password,
        }
      );

      const data = response.data;

      /* -----------------------------------------------------
         VALIDATE RESPONSE
         ----------------------------------------------------- */

      if (
        !data ||
        !data.token ||
        !data.user
      ) {
        throw new Error(
          "Invalid response from server"
        );
      }

      /* =====================================================
         STORE SESSION
         ===================================================== */

      localStorage.setItem(
        "token",
        data.token
      );

      localStorage.setItem(
        "userId",
        data.user._id
      );

      localStorage.setItem(
        "user",
        JSON.stringify(data.user)
      );

      localStorage.setItem(
        "role",
        data.user.role
      );

      /* -----------------------------------------------------
         WEEK STATUS
         ----------------------------------------------------- */

      if (data.weekStatus) {
        localStorage.setItem(
          "weekStatus",
          data.weekStatus
        );
      } else {
        localStorage.removeItem(
          "weekStatus"
        );
      }

      /* -----------------------------------------------------
         RETURN FULL BACKEND RESPONSE
         ----------------------------------------------------- */

      return data;
    } catch (error) {
      console.error(
        "❌ loginUser error:",
        error.response?.data ||
          error.message
      );

      /* -----------------------------------------------------
         RETURN BACKEND ERROR OBJECT
         ----------------------------------------------------- */

      return rejectWithValue(
        error.response?.data || {
          message:
            error.message ||
            "Login failed",
        }
      );
    }
  }
);

/* =========================================================
   VERIFY TOKEN
   ========================================================= */

export const verifyToken = createAsyncThunk(
  "auth/verifyToken",

  async (
    _,
    { getState, rejectWithValue }
  ) => {
    try {
      /* -----------------------------------------------------
         GET TOKEN
         Prefer Redux, fallback to localStorage
         ----------------------------------------------------- */

      const state = getState();

      const token =
        state.auth.token ||
        localStorage.getItem("token");

      if (!token) {
        throw new Error(
          "No token found"
        );
      }

      /* -----------------------------------------------------
         VERIFY REQUEST
         ----------------------------------------------------- */

      const response = await axios.post(
        `${baseUrl}/auth/verify-token`,
        {
          token,
        }
      );

      const data = response.data;

      /* -----------------------------------------------------
         VALIDATE RESPONSE
         ----------------------------------------------------- */

      if (
        !data ||
        !data.user
      ) {
        throw new Error(
          "Token is invalid or expired"
        );
      }

      /* -----------------------------------------------------
         RETURN FULL RESPONSE
         ----------------------------------------------------- */

      return {
        ...data,
        token,
      };
    } catch (error) {
      console.error(
        "❌ verifyToken error:",
        error.response?.data ||
          error.message
      );

      return rejectWithValue(
        error.response?.data || {
          message:
            error.message ||
            "Token verification failed",
        }
      );
    }
  }
);

/* =========================================================
   AUTH SLICE
   ========================================================= */

const authSlice = createSlice({
  name: "auth",

  initialState: {
    user: safeParse("user"),

    token:
      localStorage.getItem("token") ||
      null,

    userId:
      localStorage.getItem("userId") ||
      null,

    role:
      localStorage.getItem("role") ||
      null,

    weekStatus:
      localStorage.getItem("weekStatus") ||
      null,

    loading: false,

    error: null,

    isAuthenticated:
      !!localStorage.getItem("token"),
  },

  /* =======================================================
     REDUCERS
     ======================================================= */

  reducers: {
    /* -------------------------------------------------------
       LOGOUT
       ------------------------------------------------------- */

    logoutUser: (state) => {
      /* Clear Redux state */

      state.user = null;
      state.token = null;
      state.userId = null;
      state.role = null;
      state.weekStatus = null;
      state.loading = false;
      state.error = null;
      state.isAuthenticated = false;

      /* -----------------------------------------------------
         Clear ONLY authentication storage
         ----------------------------------------------------- */

      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("userId");
      localStorage.removeItem("role");
      localStorage.removeItem("weekStatus");

      /* -----------------------------------------------------
         Redirect
         ----------------------------------------------------- */

      window.location.href = "/login";
    },

    /* -------------------------------------------------------
       CLEAR ERROR
       ------------------------------------------------------- */

    clearError: (state) => {
      state.error = null;
    },
  },

  /* =======================================================
     EXTRA REDUCERS
     ======================================================= */

  extraReducers: (builder) => {
    builder

      /* =====================================================
         LOGIN - PENDING
         ===================================================== */

      .addCase(
        loginUser.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      /* =====================================================
         LOGIN - FULFILLED
         ===================================================== */

      .addCase(
        loginUser.fulfilled,
        (state, action) => {
          const data =
            action.payload || {};

          const user =
            data.user || null;

          state.loading = false;
          state.error = null;

          /* -------------------------------------------------
             User
             ------------------------------------------------- */

          state.user = user
            ? {
                ...user,
                contact:
                  user.contact || null,
              }
            : null;

          /* -------------------------------------------------
             Token
             ------------------------------------------------- */

          state.token =
            data.token ||
            localStorage.getItem(
              "token"
            );

          /* -------------------------------------------------
             User ID
             ------------------------------------------------- */

          state.userId =
            user?._id ||
            localStorage.getItem(
              "userId"
            );

          /* -------------------------------------------------
             Role
             ------------------------------------------------- */

          state.role =
            user?.role ||
            localStorage.getItem(
              "role"
            );

          /* -------------------------------------------------
             Week Status
             ------------------------------------------------- */

          state.weekStatus =
            data.weekStatus ||
            null;

          /* -------------------------------------------------
             Authentication
             ------------------------------------------------- */

          state.isAuthenticated =
            !!state.token;
        }
      )

      /* =====================================================
         LOGIN - REJECTED
         ===================================================== */

      .addCase(
        loginUser.rejected,
        (state, action) => {
          state.loading = false;

          /* -------------------------------------------------
             IMPORTANT:
             Clear authentication state after failed login
             ------------------------------------------------- */

          state.isAuthenticated = false;

          state.error =
            action.payload?.message ||
            action.payload?.error ||
            action.error?.message ||
            "Login failed";
        }
      )

      /* =====================================================
         VERIFY TOKEN - PENDING
         ===================================================== */

      .addCase(
        verifyToken.pending,
        (state) => {
          state.loading = true;
          state.error = null;
        }
      )

      /* =====================================================
         VERIFY TOKEN - FULFILLED
         ===================================================== */

      .addCase(
        verifyToken.fulfilled,
        (state, action) => {
          const data =
            action.payload || {};

          const user =
            data.user || null;

          state.loading = false;
          state.error = null;

          /* -------------------------------------------------
             User
             ------------------------------------------------- */

          state.user = user;

          /* -------------------------------------------------
             Token
             ------------------------------------------------- */

          state.token =
            data.token ||
            state.token ||
            localStorage.getItem(
              "token"
            );

          /* -------------------------------------------------
             User ID
             ------------------------------------------------- */

          state.userId =
            user?._id ||
            localStorage.getItem(
              "userId"
            );

          /* -------------------------------------------------
             Role
             ------------------------------------------------- */

          state.role =
            user?.role ||
            localStorage.getItem(
              "role"
            );

          /* -------------------------------------------------
             Week Status
             ------------------------------------------------- */

          state.weekStatus =
            data.weekStatus ||
            localStorage.getItem(
              "weekStatus"
            ) ||
            null;

          /* -------------------------------------------------
             Authentication
             ------------------------------------------------- */

          state.isAuthenticated =
            !!state.token;

          /* -------------------------------------------------
             Keep localStorage user synchronized
             ------------------------------------------------- */

          if (user) {
            localStorage.setItem(
              "user",
              JSON.stringify(user)
            );
          }
        }
      )

      /* =====================================================
         VERIFY TOKEN - REJECTED
         ===================================================== */

      .addCase(
        verifyToken.rejected,
        (state, action) => {
          state.loading = false;

          state.error =
            action.payload?.message ||
            action.payload?.error ||
            action.error?.message ||
            "Token verification failed";

          state.isAuthenticated = false;

          /* -------------------------------------------------
             Clear invalid authentication
             ------------------------------------------------- */

          state.user = null;
          state.token = null;
          state.userId = null;
          state.role = null;
          state.weekStatus = null;

          localStorage.removeItem(
            "token"
          );

          localStorage.removeItem(
            "user"
          );

          localStorage.removeItem(
            "userId"
          );

          localStorage.removeItem(
            "role"
          );

          localStorage.removeItem(
            "weekStatus"
          );
        }
      );
  },
});

/* =========================================================
   EXPORT ACTIONS
   ========================================================= */

export const {
  logoutUser,
  clearError,
} = authSlice.actions;

/* =========================================================
   EXPORT REDUCER
   ========================================================= */

export default authSlice.reducer;

